import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { ErrorNotice, Field, Modal, useSubmit } from '../components/ui';

interface Providers { password: boolean; entra: { label: string } | null }

// Sign-in errors arrive as a code only, so the page never shows text taken from the URL.
const SSO_ERRORS: Record<string, string> = {
  NOT_REGISTERED: 'Your Microsoft account does not have access to Inventory yet. Ask an administrator to add you.',
  INACTIVE: 'Your account is deactivated. Contact an administrator.',
  WRONG_TENANT: 'That Microsoft account belongs to a different organisation.',
  STATE_MISMATCH: 'The sign-in was started in another browser or tab. Please try again.',
  STATE_EXPIRED: 'The sign-in took too long. Please try again.',
  ALREADY_LINKED: 'This account is linked to a different Microsoft identity. Contact an administrator.',
  NO_ROLE: 'Inventory has no role to give your account. Ask an administrator to add you.',
  NO_EMAIL: 'Your Microsoft account did not share an email address. Contact an administrator.',
  PROVIDER_ERROR: 'Microsoft did not complete the sign-in. Please try again.',
  TOKEN_INVALID: 'The Microsoft sign-in could not be verified. Please try again.',
};

function useProviders() {
  const [providers, setProviders] = useState<Providers | null>(null);
  useEffect(() => {
    api.get<Providers>('/auth/providers').then(setProviders).catch(() => setProviders({ password: true, entra: null }));
  }, []);
  return providers;
}

export const microsoftSignInUrl = (returnTo = window.location.pathname + window.location.search) =>
  `/api/v1/auth/sso/entra/start?returnTo=${encodeURIComponent(returnTo)}`;

function MicrosoftButton({ label, returnTo }: { label: string; returnTo?: string }) {
  return (
    <a className="button primary" href={microsoftSignInUrl(returnTo)} style={{ width: '100%' }}>
      <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
        <rect x="1" y="1" width="9" height="9" fill="#f25022" /><rect x="11" y="1" width="9" height="9" fill="#7fba00" />
        <rect x="1" y="11" width="9" height="9" fill="#00a4ef" /><rect x="11" y="11" width="9" height="9" fill="#ffb900" />
      </svg>
      {label}
    </a>
  );
}

export default function Login() {
  const { signIn } = useSession();
  const providers = useProviders();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { submit, busy, error } = useSubmit(() => signIn(email.trim(), password));
  // Read once: the code is removed from the address bar below, and later renders must keep showing it.
  const [ssoError] = useState(() => new URLSearchParams(window.location.search).get('sso_error'));

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void submit();
  };

  // Clear the error code from the address bar once shown.
  useEffect(() => {
    if (ssoError) window.history.replaceState(null, '', window.location.pathname);
  }, [ssoError]);

  const passwordForm = providers?.password || showPassword || !providers?.entra;
  return (
    <main className="login">
      <div className="card">
        <div className="cardbody">
          <img src="/hotel101-green.png" alt="Hotel101" />
          <h1 style={{ textAlign: 'center', marginBottom: 20 }}>Inventory sign in</h1>
          {ssoError && <div className="notice error" role="alert">{SSO_ERRORS[ssoError] ?? 'Microsoft sign-in failed. Please try again.'}</div>}
          {providers?.entra && (
            <>
              <MicrosoftButton label={providers.entra.label} returnTo="/" />
              {passwordForm && <p className="muted small" style={{ textAlign: 'center', margin: '16px 0' }}>or sign in with a password</p>}
            </>
          )}
          {passwordForm ? (
            <form onSubmit={onSubmit}>
              <ErrorNotice error={error} />
              <div className="form" style={{ gridTemplateColumns: '1fr' }}>
                <Field label="Email">{id => <input id={id} type="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} required autoFocus={!providers?.entra} />}</Field>
                <Field label="Password">{id => <input id={id} type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} required />}</Field>
                <button className={providers?.entra ? '' : 'primary'} disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
              </div>
            </form>
          ) : (
            <p style={{ textAlign: 'center', marginTop: 16 }}>
              <button className="link small" onClick={() => setShowPassword(true)}>Administrator password sign-in</button>
            </p>
          )}
        </div>
      </div>
    </main>
  );
}

/** Shown over the current page when the session ends, so unsaved work is not lost. */
export function SessionExpired({ email }: { email: string }) {
  const { signIn, signOut } = useSession();
  const providers = useProviders();
  const [password, setPassword] = useState('');
  const { submit, busy, error } = useSubmit(() => signIn(email, password));
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void submit();
  };
  return (
    <Modal title="Your session has ended" onClose={() => {}} dismissable={false} footer={<>
      <button onClick={() => void signOut()}>Sign out</button>
      <button className="primary" form="reauth" disabled={busy || !password}>{busy ? 'Signing in…' : 'Sign in and continue'}</button>
    </>}>
      <form id="reauth" onSubmit={onSubmit}>
        <p>Sign in again as <strong>{email}</strong> to continue. Your unsaved changes on this page are kept if you use your password.</p>
        {providers?.entra && (
          <div style={{ marginBottom: 16 }}>
            <MicrosoftButton label={providers.entra.label} />
            <p className="muted small" style={{ marginTop: 6 }}>Microsoft sign-in reloads this page; save or note any unsaved changes first.</p>
          </div>
        )}
        <ErrorNotice error={error} />
        <Field label="Password">{id => <input id={id} type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} autoFocus />}</Field>
      </form>
    </Modal>
  );
}
